import type {
  ChannelRevenueMonthly,
  ChiaNhanSuKy,
  ExchangeRate,
  ExchangeRateInput,
  MusicLicenseCost,
  MusicLicenseCostInput,
  MusicRevenue,
  MusicRevenueInput,
  MusicSource,
  ReconciliationIssue,
  ResolveReconciliationInput,
  RevenueOverview,
  RevenueOverviewKenh,
} from '@qlk/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, goiApi, type LoiApi } from '../../lib/api.js';
import { goiDuocApi, khoaKy, type KyTrang } from '../../lib/kyTrang.js';

const GOC = '/api/v1/revenue';

export const KHOA = {
  tongQuan: (period: string, months: number) => ['revenue', 'overview', period, months] as const,
  tongQuanKenh: (period: string, months: number) =>
    ['revenue', 'overview-kenh', period, months] as const,
  kenh: (period: string) => ['revenue', 'channels', period] as const,
  kenhTheoKy: (channelId: string, ky: string) =>
    ['revenue', 'channels', 'theo-ky', channelId, ky] as const,
  chiaNhanSu: (ky: string) => ['revenue', 'chia-nhan-su', ky] as const,
  chiaNhanSuKenh: (channelId: string, ky: string) =>
    ['revenue', 'chia-nhan-su', 'kenh', channelId, ky] as const,
  doiSoat: ['revenue', 'reconciliation'] as const,
  nhac: (period?: string) => ['revenue', 'music', period ?? ''] as const,
  phiNhac: (period?: string) => ['revenue', 'music-cost', period ?? ''] as const,
  nguonNhac: ['revenue', 'music-sources'] as const,
  tyGia: ['revenue', 'rates'] as const,
};

/**
 * Kỳ trên URL theo **đúng quy ước chung của app**: `?ky=<mã>` cho kỳ tháng
 * (máy chủ tự suy khoảng, và mã tháng mới đi được đường ảnh chụp lúc chốt),
 * `?from=&to=` cho kỳ khoảng.
 *
 * Không có tham số `?period=` riêng: hai endpoint chia nhân sự đọc kỳ bằng
 * `kyTuYeuCau` như mọi endpoint khác.
 */
function thamSoKy(ky: KyTrang | string): string {
  if (typeof ky === 'string') return thamSo({ ky });
  return ky.loai === 'thang' ? thamSo({ ky: ky.ma }) : thamSo({ from: ky.from, to: ky.to });
}

/** Khoá cache của một kỳ, nhận cả mã tháng trần (trang Doanh thu) và `KyTrang`. */
function khoaCuaKy(ky: KyTrang | string): string {
  return typeof ky === 'string' ? ky : khoaKy(ky);
}

/** Kỳ đã đủ dữ liệu để gọi API chưa; mã tháng trần thì gọi được ngay. */
function kyGoiDuoc(ky: KyTrang | string): boolean {
  return typeof ky === 'string' ? ky !== '' : goiDuocApi(ky);
}

function thamSo(obj: Record<string, string | number | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(obj)) if (v !== undefined && v !== '') sp.set(k, String(v));
  const chuoi = sp.toString();
  return chuoi ? `?${chuoi}` : '';
}

/* -------------------------------- Đọc -------------------------------- */

/**
 * Tổng quan **toàn công ty**. `bat = false` cho người bị giới hạn theo kênh
 * được giao: máy chủ trả 403 cho họ (rà soát bảo mật 16/10/2026, mục V4a), nên
 * gọi vào chỉ để lấy một khung đỏ. Họ dùng `useTongQuanKenh`.
 */
export function useTongQuanDoanhThu(period: string, months = 6, bat = true) {
  return useQuery<RevenueOverview, LoiApi>({
    queryKey: KHOA.tongQuan(period, months),
    queryFn: () => api.get<RevenueOverview>(`${GOC}/overview${thamSo({ period, months })}`),
    enabled: bat,
  });
}

/** Tổng quan **chỉ các kênh được xem** — cùng công thức, tập kênh nhỏ hơn. */
export function useTongQuanKenh(period: string, months = 6, bat = true) {
  return useQuery<RevenueOverviewKenh, LoiApi>({
    queryKey: KHOA.tongQuanKenh(period, months),
    queryFn: () =>
      api.get<RevenueOverviewKenh>(`${GOC}/tong-quan-kenh${thamSo({ period, months })}`),
    enabled: bat,
  });
}

/**
 * `batDau = false` thì không gọi: trang kênh dùng hook này cho dải chỉ số, mà
 * người không có `revenue:view` chỉ nhận được 403.
 */
export function useDoanhThuKenh(period: string, batDau = true) {
  return useQuery<ChannelRevenueMonthly[], LoiApi>({
    queryKey: KHOA.kenh(period),
    queryFn: () => api.get<ChannelRevenueMonthly[]>(`${GOC}/kenh${thamSo({ period })}`),
    enabled: batDau,
  });
}

/**
 * Dòng doanh thu của **một kênh** cho mọi tháng mà kỳ khoảng chạm vào.
 *
 * Khối "Doanh thu chi tiết" là sổ sao kê theo tháng: không cắt được ra từng
 * ngày, nên kỳ "90 ngày qua" hiện **ba** dòng tháng thay vì một dòng của tháng
 * chứa ngày cuối kỳ — một bảng một dòng cho kỳ ba tháng là bảng nói sai về kỳ.
 */
export function useDoanhThuKenhTheoKy(channelId: string, ky: KyTrang, batDau = true) {
  return useQuery<ChannelRevenueMonthly[], LoiApi>({
    queryKey: KHOA.kenhTheoKy(channelId, khoaKy(ky)),
    queryFn: () =>
      api.get<ChannelRevenueMonthly[]>(
        `${GOC}/kenh/${channelId}/theo-ky${thamSo({ from: ky.from, to: ky.to })}`,
      ),
    enabled: batDau && ky.sanSang,
  });
}

/**
 * Phần chia nhân sự của kỳ, theo kênh — ô thống kê, cột chia của bảng "Theo
 * kênh" và khối "Chia nhân sự theo người" của module Doanh thu.
 *
 * Máy chủ **đã che** theo người xem: `tong.chiaNhanSu` / `tong.congTyGiuLai` và
 * `theoNguoi` chỉ có với người xem được trang Đối soát. Giao diện đọc
 * `xemDayDu` để chọn tập cột, **không** tự suy quyền.
 */
export function useChiaNhanSu(ky: KyTrang | string, batDau = true) {
  return useQuery<ChiaNhanSuKy, LoiApi>({
    queryKey: KHOA.chiaNhanSu(khoaCuaKy(ky)),
    queryFn: () => api.get<ChiaNhanSuKy>(`${GOC}/chia-nhan-su${thamSoKy(ky)}`),
    enabled: batDau && kyGoiDuoc(ky),
  });
}

/** Cùng dữ liệu, gói về **một kênh** — tab "Doanh thu & chia sẻ" của trang kênh. */
export function useChiaNhanSuKenh(channelId: string, ky: KyTrang | string, batDau = true) {
  return useQuery<ChiaNhanSuKy, LoiApi>({
    queryKey: KHOA.chiaNhanSuKenh(channelId, khoaCuaKy(ky)),
    queryFn: () => api.get<ChiaNhanSuKy>(`${GOC}/kenh/${channelId}/chia-nhan-su${thamSoKy(ky)}`),
    enabled: batDau && kyGoiDuoc(ky),
  });
}

export function useDoiSoat() {
  return useQuery<ReconciliationIssue[], LoiApi>({
    queryKey: KHOA.doiSoat,
    queryFn: () => api.get<ReconciliationIssue[]>(`${GOC}/doi-soat`),
  });
}

export function useDoanhThuNhac(period?: string) {
  return useQuery<MusicRevenue[], LoiApi>({
    queryKey: KHOA.nhac(period),
    queryFn: () => api.get<MusicRevenue[]>(`${GOC}/nhac${thamSo({ period })}`),
  });
}

export function usePhiNhac(period?: string) {
  return useQuery<MusicLicenseCost[], LoiApi>({
    queryKey: KHOA.phiNhac(period),
    queryFn: () => api.get<MusicLicenseCost[]>(`${GOC}/phi-nhac${thamSo({ period })}`),
  });
}

/** Nguồn nhạc cho ô chọn — API của revenue gọi `music.listSources` hộ. */
export function useNguonNhac() {
  return useQuery<MusicSource[], LoiApi>({
    queryKey: KHOA.nguonNhac,
    queryFn: () => api.get<MusicSource[]>(`${GOC}/nguon-nhac`),
  });
}

export function useTyGia() {
  return useQuery<ExchangeRate[], LoiApi>({
    queryKey: KHOA.tyGia,
    queryFn: () => api.get<ExchangeRate[]>(`${GOC}/ty-gia`),
  });
}

/* ------------------------------ Mutation ----------------------------- */

function lamMoi(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({ queryKey: ['revenue'] });
}

export function useThemDoanhThuNhac() {
  const qc = useQueryClient();
  return useMutation<MusicRevenue, LoiApi, MusicRevenueInput>({
    mutationFn: (input) => api.post<MusicRevenue>(`${GOC}/nhac`, input),
    onSuccess: () => lamMoi(qc),
  });
}

export function useLuuPhiNhac() {
  const qc = useQueryClient();
  return useMutation<MusicLicenseCost, LoiApi, MusicLicenseCostInput>({
    mutationFn: (input) => api.post<MusicLicenseCost>(`${GOC}/phi-nhac`, input),
    onSuccess: () => lamMoi(qc),
  });
}

export function useLuuTyGia() {
  const qc = useQueryClient();
  return useMutation<ExchangeRate, LoiApi, ExchangeRateInput>({
    mutationFn: (input) => api.post<ExchangeRate>(`${GOC}/ty-gia`, input),
    onSuccess: () => lamMoi(qc),
  });
}

export function useXuLyDoiSoat() {
  const qc = useQueryClient();
  return useMutation<
    ChannelRevenueMonthly,
    LoiApi,
    { channelId: string; period: string } & ResolveReconciliationInput
  >({
    mutationFn: ({ channelId, period, ...input }) =>
      api.post<ChannelRevenueMonthly>(`${GOC}/doi-soat/${channelId}/${period}`, input),
    onSuccess: () => lamMoi(qc),
  });
}

/**
 * Đánh dấu kênh đã liên kết với tài khoản AdSense của công ty.
 * Việc liên kết thật làm thủ công trong YouTube Studio.
 */
export function useDanhDauDaLienKet() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, LoiApi, string>({
    mutationFn: (channelId) => api.post<{ ok: true }>(`${GOC}/kenh/${channelId}/adsense`),
    onSuccess: async () => {
      await lamMoi(qc);
      await qc.invalidateQueries({ queryKey: ['networks'] });
      await qc.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}

export function useChotThang() {
  const qc = useQueryClient();
  return useMutation<{ period: string; channels: number; warnings: number }, LoiApi, string>({
    mutationFn: (period) =>
      goiApi<{ period: string; channels: number; warnings: number }>(
        `${GOC}/chot-thang/${period}`,
        { method: 'POST' },
      ),
    onSuccess: () => lamMoi(qc),
  });
}

/** Đường dẫn tải file Excel — mở bằng thẻ `a` để trình duyệt tự tải. */
export function duongDanXuatExcel(period: string, withVnd = true): string {
  return `${GOC}/xuat-excel${thamSo({ period, withVnd: String(withVnd) })}`;
}
